import SubmitButton from "components/Buttons/SubmitButton";
import CancelBackButton from "components/Buttons/CancelBackButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { addGst, fetchGstById, updateGst } from "slices/gst/thunk";
import * as Yup from "yup";
import PageContainer from "components/PageContainer/PageContainer";

const GSTForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [initialData, setInitialData] = useState({
    GSTName: "",
    remark: "",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useDispatch<any>();

  useEffect(() => {
    if (isEdit && id) {
      setIsLoading(true);
      dispatch(fetchGstById(id))
        .unwrap()
        .then((data: any) =>
          setInitialData({
            GSTName: data.gst_percent,
            remark: data.remark,
          })
        )
        .catch((err: any) => {
          const errorMessage =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.error ||
            err?.message ||
            "Failed to load GST data";
          toast.error(errorMessage);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [id, isEdit]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      GSTName: initialData.GSTName || "",
      remark: initialData.remark || "",
    },
    validationSchema: Yup.object({
      GSTName: Yup.string()
        .required("Please enter GST %")
        .matches(/^\d*\.?\d+$/, "Only numeric values are allowed"),
    }),
    onSubmit: async (values) => {
      const payload = {
        gst_percent: values.GSTName,
        remark: values.remark,
      };

      try {
        if (isEdit) {
          await dispatch(updateGst({ id, ...payload })).unwrap();
          toast.success("GST updated successfully");
        } else {
          await dispatch(addGst(payload)).unwrap();
          toast.success("GST added successfully");
        }
        navigate("/gst", { state: { clearState: !isEdit } });
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.error ||
          error?.message ||
          "Something went wrong. Please try again.";
        toast.error(errorMessage);
      }
    },
  });

  return (
    <PageContainer>
      <FormHeader
        title={isEdit ? "Edit GST" : "Add GST"}
        firstLabel="GST"
        secondLabel={isEdit ? "Edit GST" : "Add GST"}
        handleArrowClick={() => navigate("/gst")}
      />
      {isEdit && isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <Form autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row>
            <Col lg={6}>
              <div className="mb-3">
                <Form.Label>
                  GST % <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="GSTName"
                  placeholder="Enter GST %"
                  value={formik.values.GSTName}
                  onChange={formik.handleChange}
                  onInput={(e: React.FormEvent<HTMLInputElement>) => {
                    const value = e.currentTarget.value;
                    // Allow only digits and one optional dot
                    if (!/^\d*\.?\d*$/.test(value)) {
                      e.preventDefault();
                      return;
                    }
                    formik.setFieldValue("GSTName", value);
                  }}
                  onBlur={formik.handleBlur}
                  isInvalid={!!formik.errors.GSTName && formik.touched.GSTName}
                  style={{
                    height: "40px",
                    lineHeight: "1.5",
                    fontSize: "0.875rem",
                  }}
                />

                <Form.Control.Feedback type="invalid">
                  {formik.errors.GSTName}
                </Form.Control.Feedback>
              </div>
            </Col>

            <Col lg={6}>
              <div className="mb-3">
                <Form.Label>
                  Remark
                  {/* <span className="text-danger">*</span> */}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="remark"
                  placeholder="Enter Remark"
                  value={formik.values.remark}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={!!formik.errors.remark && formik.touched.remark}
                  style={{
                    height: "40px",
                    lineHeight: "1.5",
                    fontSize: "0.875rem",
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.remark}
                </Form.Control.Feedback>
              </div>
            </Col>
          </Row>

          <div className="d-flex justify-content-center gap-3 py-3">
            <CancelBackButton to="/gst" />

            {/* Reusable submit button with spinner */}
            <SubmitButton isSubmitting={formik.isSubmitting} isEdit={isEdit} />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default GSTForm;
